import { number, func, arrayOf } from "prop-types";
import { useTranslation } from "react-i18next";
import { MAX_DESCRIPTION_COLUMNS } from "@repo/utils";
import { useXlsImporterSettings } from "@tools";
import { AdditionalDescriptionColumn, ColumnSelector } from "../components";
import { AddButtonFull, InfoLabel } from "@components";
import { Grid, useTheme } from "@material-ui/core";
import classnames from "classnames";

export const DescriptionColumnSelector = ({
  colorColumn,
  availableColumns,
  takenColumns,
  ...columnSelectorProps
}) => {
  const { t } = useTranslation();
  const settings = useXlsImporterSettings();
  const theme = useTheme();
  
  const {
    descColumn,
    additionalTextColumns,
    setAdditionalTextColumns,
    useFastMode,
    useMinMax,
  } = settings;
  
  const handleDescriptionColumnSelect = (e) => {
    if(typeof descColumn === "number")
      colorColumn([descColumn], true, true, theme.props.selections.alt);
    if(typeof e.target.value === "number")
      colorColumn([e.target.value], true, false, theme.props.selections.alt);
    settings.setImportDescColumn(e.target.value);
  }

  const getNextTextColumn = () => {
    const availableColumnsToRight = availableColumns.filter(c => (
      c > Math.max(...additionalTextColumns.map(t => t.column), descColumn) &&
      !takenColumns.includes(c)
    ));
    const availableFreeColumns = availableColumns.filter(c => !takenColumns.includes(c));

    return availableColumnsToRight[0] ?? availableFreeColumns[0] ?? null;
  };

  const addTextColumn = () => {
    const column = getNextTextColumn();
    const nextIndex = additionalTextColumns.length + 2;

    setAdditionalTextColumns([
      ...additionalTextColumns,
      {
        name: t("views.import.next_text_column", { index: nextIndex }),
        column,
      },
    ]);

    if(typeof column === "number")
      colorColumn([column], true, false, theme.props.selections.alt);
  };

  const canAddTextColumn = (
    additionalTextColumns.length < MAX_DESCRIPTION_COLUMNS - 1 &&
    availableColumns.some(c => !takenColumns.includes(c))
  );
  
  return <Grid item container direction="column">
    <InfoLabel spacing={0} label="views.import.sub_titles.description" className="text-secondary text-xs mb-1" />
    <Grid
      item container
      alignItems="center"
      wrap="nowrap"
      className="-mx-2"
    >
      <Grid
        item container
        alignItems="center"
        wrap="nowrap"
        className={classnames(
          "gap-3 rounded w-max min-w-max py-1 px-2",
          useFastMode && "w-max",
        )}
      >
        <p className="capitalize font-bold text-md">
          {t("views.import.sub_titles.description")}
        </p>
        <ColumnSelector
          name="desc"
          allowClearValue
          availableColumns={availableColumns}
          takenColumns={takenColumns}
          {...columnSelectorProps}
          selectedColumn={descColumn}
          onChange={handleDescriptionColumnSelect}
          useMinMax={useMinMax}
          size="large"
        />
      </Grid>
      {
        additionalTextColumns.map((textColumn, index) => (
          <AdditionalDescriptionColumn
            index={index}
            name={textColumn.name}
            column={textColumn.column}
            colorColumn={colorColumn}
            availableColumns={availableColumns}
            takenColumns={takenColumns}
            {...columnSelectorProps}
          />
        ))
      }
      <Grid item>
        <AddButtonFull
          color="secondary"
          onClick={addTextColumn}
          className="w-max"
          disabled={!canAddTextColumn}
        >
          {t("views.import.text_column_add")}
        </AddButtonFull>
      </Grid>
    </Grid>
  </Grid>
}

DescriptionColumnSelector.propTypes = {
  availableColumns: arrayOf(number).isRequired,
  takenColumns: arrayOf(number).isRequired,
  colorColumn: func.isRequired,
}
